import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useGlobalBackButton } from '../../GlobalBackButtonContext';
import {
    fetchBranches,
    fetchDoctors,
    resetSelectedFilters,
    selectBranchSimplified,
    selectDate,
    selectDoctor,
} from './filtersSlice';
import { selectFiltersMetadata } from './selectors';
import { GroupInput } from '../../components/GroupInput/GroupInput';
import { useWidgetProps } from '../../WidgetPropsContext';
import { getAssigmentsInfo } from '../../api';

import './filters.scss';
import { selectTimeslots } from '../doctors/selectors';
import { convertTimeZone, formatFullDate, SERVER_TIMEZONE } from '../../utils';
import { findDoctorById } from './utils';
import { fetchTimeslots, fetchTimetable } from '../doctors/doctorsSlice';
import { clearDoctorBookingError } from '../bookingForm/bookingFormSlice';

const filters = require('b_')
    .with('filters');

const START_TO_TYPE = 'Начните вводить данные';

const addMonths = (date, months) => {
    const monthSum = date.getMonth() + months;
    const newMonth = (12 + (monthSum % 12)) % 12;
    const newYear = date.getFullYear() + Math.floor(monthSum / 12);
    const newDate = new Date(newYear, newMonth, date.getDate());
    return (newDate.getMonth() !== newMonth)
        ? new Date(newDate.setDate(0))
        : newDate;
};
const getEndDate = (d) => {
    return addMonths(convertTimeZone(d, null, SERVER_TIMEZONE), 2);
};

// eslint-disable-next-line max-statements
export const DoctorFilters = ({
    isTimeslotOccupied = false,
    error: _error,
    onBack,
    specialityName,
    doctorName,
    targetDoctorId,
}) => {
    const {
        baseUrl,
        branchId: _branchId,
        currentBranchId,
    } = useWidgetProps();
    const dispatch = useDispatch();
    const {
        pathname,
        state = {},
    } = useLocation();
    const history = useHistory();

    const {
        isLoading,
        error,
        branches,
        selectedBranch,
        selectedDoctor,
        selectedSpecialization,
        date,
        fulldoctors,
    } = useSelector(selectFiltersMetadata);

    const { setOnBack } = useGlobalBackButton();

    const doctorsList = React.useMemo(
        () => (fulldoctors ?? []).map(d => ({
            name: d.fullName,
            id: d.doctorId,
        })),
        [fulldoctors],
    );

    const branchesForSelectedDoctor = React.useMemo(() => {
        if (!selectedDoctor) {return [];}
        const doctor = fulldoctors?.find(d => d.doctorId === selectedDoctor);
        if (!doctor) {return [];}
        return branches.filter(branch => doctor.branches.includes(branch.id));
    }, [ selectedDoctor, fulldoctors, branches ]);

    const onBackHandler = useCallback(() => {
        if (onBack) {
            onBack();
        }
        if (!selectedBranch) {
            dispatch(resetSelectedFilters());
        }
    }, [ onBack, selectedBranch ]);

    useEffect(() => {
        setOnBack(() => onBackHandler);
        return () => setOnBack(null);
    }, [ onBackHandler, setOnBack ]);

    const timetable = useSelector(selectTimeslots);

    // useEffect(() => {
    //     if (baseUrl) {
    //         if (state.reloadBranches !== false && (currentBranchId || currentBranchId === null)) {
    //             dispatch(
    //                 fetchBranches({
    //                     baseUrl,
    //                     currentBranchId,
    //                 }),
    //             );
    //             dispatch(
    //                 fetchDoctors({
    //                     baseUrl,
    //                     currentBranchId,
    //                 }),
    //             );
    //         }
    //     }
    // }, [ baseUrl, currentBranchId, dispatch ]);

    const redirectToTheHomePage = useCallback((samePage = false) => {
        const isHomePathname = pathname.length <= 1;
        dispatch(clearDoctorBookingError());
        if (samePage) {
            history.push(pathname, {
                ...state,
                errorMessage: undefined,
            });
        } else if (!isHomePathname) {
            history.push('/', {
                ...state,
                errorMessage: undefined,
            });
        }
    }, [ history, pathname.length, state ]);

    const onDoctorSelect = useCallback(
        (doctorId) => {
            dispatch(selectDoctor(doctorId));
            redirectToTheHomePage();
        },
        [ dispatch, redirectToTheHomePage, selectedSpecialization ],
    );

    useEffect(() => {
        if (selectedDoctor && selectedBranch) {
            dispatch(fetchTimeslots({
                branch: selectedBranch,
                date,
                baseUrl,
                specializationId: selectedSpecialization,
            }));
            // }
        }
    }, [ selectedBranch, selectedSpecialization ]);

    const onBranchSelect = useCallback(
        (branchId) => {
            dispatch(selectBranchSimplified(branchId));
            // dispatch(clearTimetableRequestParams());
            // redirectToTheHomePage();
        },
        [ dispatch, redirectToTheHomePage ],
    );

    useEffect(() => {
        if (
            branchesForSelectedDoctor.length === 1
        ) {
            onBranchSelect(branchesForSelectedDoctor[0].id);
        }
    }, [branchesForSelectedDoctor]);

    useEffect(() => {
        if (fulldoctors && (doctorName || targetDoctorId) && !selectedDoctor) {
            const found = doctorName ? fulldoctors?.find(({ fullName, specialityName: _specialityName }) => fullName?.toLowerCase() === doctorName?.toLowerCase() && (!specialityName || specialityName?.toLowerCase() === _specialityName?.toLowerCase())) : fulldoctors?.find(({ doctorId }) => doctorId === targetDoctorId);
            if (found) {
                onDoctorSelect(found?.doctorId);
                if (_branchId) {
                    onBranchSelect(_branchId);
                }
            }
        }
    }, [ fulldoctors, specialityName, doctorName, _branchId, targetDoctorId ]);

    const onDateSelect = useCallback(
        (dateValue) => {
            dispatch(selectDate(dateValue));
            redirectToTheHomePage();
        },
        [ dispatch, redirectToTheHomePage, selectedSpecialization, selectedDoctor, selectedBranch ],
    );

    const loadTimeTable = async (doctorId, branchId, isTimeslotOccupied = false) => {
        const dateStart = formatFullDate(convertTimeZone(new Date(Date.now()), null, SERVER_TIMEZONE));
        const dateEnd = formatFullDate(getEndDate(new Date(Date.now())));
        if (doctorId && branchId) {
            const doctor = findDoctorById(fulldoctors, doctorId);
            const doctorAssignments = doctor?.assignments ?? [];
            try {
                let assignment = doctorAssignments[0];
                if (doctorAssignments.length > 1) {
                    try {
                        assignment = (await getAssigmentsInfo(doctorAssignments))
                            ?.find(item => item?.branchId === branchId)?.assignmentId;
                    } catch (e) {
                        // console.log('Error get assignments', e);
                    }
                }
                dispatch(
                    fetchTimetable({
                        assignment,
                        doctorId,
                        dateStart,
                        dateEnd,
                        baseUrl,
                    }),
                );
            } catch (e) {
                //
            }
        }
    };

    const onOnlineState = () => {
        redirectToTheHomePage();
    };

    useEffect(() => {
        // eslint-disable-next-line no-undef
        window?.addEventListener('online', onOnlineState);
        // eslint-disable-next-line no-undef
        return () => window?.removeEventListener('online', onOnlineState);
    }, []);

    useEffect(() => {
        if (fulldoctors && selectedDoctor && selectedBranch && (!_error || _error && isTimeslotOccupied)) {
            loadTimeTable(selectedDoctor, selectedBranch, isTimeslotOccupied);
        }
    }, [ fulldoctors?.length, selectedDoctor, selectedBranch, baseUrl, isTimeslotOccupied, _error ]);
    return (
        <div className={filters()}>
            <GroupInput
                id="filter_doctors"
                type="selector"
                label="Специалист"
                onChange={onDoctorSelect}
                isLoading={isLoading}
                metaData={doctorsList}
                value={selectedDoctor}
                error={error}
                placeholder={START_TO_TYPE}
            />
            <GroupInput
                id="filter_branch"
                type="selector"
                label="Филиал"
                onChange={onBranchSelect}
                isLoading={isLoading}
                metaData={branchesForSelectedDoctor}
                value={selectedBranch}
                error={error}
                placeholder={START_TO_TYPE}
            />
            <GroupInput
                id="filter_date"
                type="datePicker"
                label="Дата"
                onChange={onDateSelect}
                value={date}
                highlightedDates={timetable?.timetable?.[selectedDoctor] ?? []}
                highlightDates={!!selectedDoctor}
            />
        </div>
    );
};
